// 세션 창의 **별도 창** 전달 수단 — 채팅 창(설계서 §9.6.6)과 파일 창(§9.4)이 함께 쓴다.
// 메시지 모양은 창마다 다르지만(각자의 규약 파일), 길은 같다:
//
//   통합앱  : Rust 명령(`open_*_window`)이 창을 만들고, 양쪽이 Tauri 이벤트(`emitTo`)로 주고받는다.
//             창이 사라지면 **Rust 가** 세션 창에 `bye` 를 보낸다(웹이 닫히기 직전에 보내면 유실될 수 있다).
//             🔴 이 길은 JSON 만 실린다 — 파일 핸들·File 객체는 보낼 수 없다(파일 창은 앱에서 그런 메시지를 쓰지 않는다).
//   브라우저: `window.open` 으로 같은 출처의 팝업을 열고 `postMessage` 로 주고받는다(구조화 복제 — 파일 핸들·File 도 실린다).
//             "창이 닫혔다" 이벤트가 없어 양쪽이 상대의 `closed` 를 주기적으로 본다.
//
// 호출부는 어느 길인지 모른다 — 분기는 이 파일 안에만 둔다. 창 종류별 값은 `PopupSpec` 한 벌로 넘긴다
// (🔴 Rust `popup_window.rs` 의 `PopupKind` 와 같은 값이어야 한다).

import { IS_SHELL } from '../shell';

/** 별도 창 한 종류의 고정 값. */
export interface PopupSpec {
  /** 창 페이지(Vite 입력 이름 + `.html`). */
  page: string;
  /** 브라우저 postMessage 봉투의 키 — 우리 메시지를 다른 스크립트의 메시지와 가려낸다(값 = 세션 키). */
  tag: string;
  /** 브라우저 창 이름 접두어(같은 세션이면 같은 창을 다시 쓴다). */
  browserPrefix: string;
  /** 브라우저 팝업 크기 — 앱 창 크기와 같게. */
  browserFeatures: string;
  /** 별도 창 → 세션 창 / 세션 창 → 별도 창 이벤트(앱). */
  evtToHost: string;
  evtToWin: string;
  /** Tauri 명령. */
  openCommand: string;
  closeCommand: string;
  /** 앱이 창에 넣어 주는 전역 이름(`window.__RS_CHAT__` 등). */
  initGlobal: string;
}

/** 브라우저 쿼리 키 — 별도 창이 자기 세션 키를 안다. */
export const KEY_PARAM = 'key';
/** 브라우저: 상대 창이 닫혔는지 보는 주기(ms). 브라우저에는 "창이 닫혔다" 이벤트가 없다. */
export const CLOSED_POLL_MS = 1000;

/** 한쪽 끝. `In` = 받는 메시지, `Out` = 보내는 메시지. */
export interface WindowLink<In, Out> {
  post(msg: Out): void;
  /** 받기 시작 — 돌려준 함수로 멈춘다. */
  onMessage(cb: (msg: In) => void): () => void;
  /** 세션 창: 별도 창을 닫는다. 별도 창: 아무것도 하지 않는다(닫기는 세션 창이 한다). */
  close(): void;
  /** 세션 창: 별도 창을 앞으로 가져온다. */
  focus(): void;
}

/** 앱이 별도 창에 넣어 주는 값(`popup_window.rs` 초기화 스크립트). */
interface ShellPopupInfo {
  host: string;
  key: string;
  label: string;
}

/** 받은 값이 이 규약의 메시지인가(모양 검사 — 규약 파일이 준다). */
type Guard<T> = (v: unknown) => v is T;

// ── 세션 창 쪽 ─────────────────────────────────────────────────────────────

/**
 * 별도 창을 연다(이미 있으면 앞으로). 열지 못하면 null — 브라우저가 팝업을 막은 경우 등.
 * @param spec  창 종류
 * @param key   이 세션의 키(`newPopupKey`)
 * @param title 창 제목
 * @param isIn  받은 메시지 모양 검사
 * @param quiet 창을 활성화하지 않는다(앱만 가능 — 브라우저 `window.open` 은 늘 포커스를 가져간다)
 */
export async function openPopup<In extends { t: string }, Out>(
  spec: PopupSpec,
  key: string,
  title: string,
  isIn: Guard<In>,
  quiet = false,
): Promise<WindowLink<In, Out> | null> {
  return IS_SHELL ? openTauri(spec, key, title, isIn, quiet) : openBrowser(spec, key, isIn);
}

async function openTauri<In extends { t: string }, Out>(
  spec: PopupSpec,
  key: string,
  title: string,
  isIn: Guard<In>,
  quiet: boolean,
): Promise<WindowLink<In, Out> | null> {
  const { invoke } = await import('@tauri-apps/api/core');
  const { emitTo } = await import('@tauri-apps/api/event');
  const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
  let label: string;
  try {
    label = await invoke<string>(spec.openCommand, { key, title, quiet });
  } catch {
    return null;
  }
  const me = getCurrentWebviewWindow();
  return {
    post: (msg) => void emitTo(label, spec.evtToWin, msg).catch(() => undefined),
    onMessage: (cb) => {
      const un = me.listen<{ key?: string; msg?: unknown }>(spec.evtToHost, (e) => {
        // 한 세션 창에 탭이 여럿이면 별도 창도 여럿이다 — 내 키만 받는다.
        if (e.payload?.key === key && isIn(e.payload.msg)) {
          cb(e.payload.msg);
        }
      });
      return () => void un.then((f) => f());
    },
    close: () => void invoke(spec.closeCommand, { key }).catch(() => undefined),
    focus: () => void invoke(spec.openCommand, { key, title }).catch(() => undefined),
  };
}

function openBrowser<In extends { t: string }, Out>(
  spec: PopupSpec,
  key: string,
  isIn: Guard<In>,
): WindowLink<In, Out> | null {
  const url = new URL(spec.page, window.location.href);
  url.search = '';
  url.searchParams.set(KEY_PARAM, key);
  const w = window.open(url.toString(), spec.browserPrefix + key, spec.browserFeatures);
  if (!w) {
    return null; // 팝업 차단
  }
  const origin = window.location.origin;
  return {
    post: (msg) => {
      try {
        w.postMessage({ [spec.tag]: key, msg }, origin);
      } catch {
        // 창이 막 닫혔다 — 감시가 bye 를 만든다
      }
    },
    onMessage: (cb) => {
      const onMsg = (e: MessageEvent) => {
        const env = e.data as Record<string, unknown> | null;
        if (e.origin === origin && e.source === w && env?.[spec.tag] === key && isIn(env.msg)) {
          cb(env.msg);
        }
      };
      window.addEventListener('message', onMsg);
      // 브라우저에는 "창이 닫혔다" 이벤트가 없다 → 주기적으로 본다(사람이 X 로 닫은 것).
      const timer = window.setInterval(() => {
        if (w.closed) {
          window.clearInterval(timer);
          cb({ t: 'bye' } as In);
        }
      }, CLOSED_POLL_MS);
      return () => {
        window.removeEventListener('message', onMsg);
        window.clearInterval(timer);
      };
    },
    close: () => w.close(),
    focus: () => w.focus(),
  };
}

// ── 별도 창 쪽 ─────────────────────────────────────────────────────────────

/** 별도 창이 세션 창에 붙는다. 세션 창 없이 열렸으면(주소를 직접 연 경우) null. */
export async function connectPopup<In extends { t: string }, Out>(
  spec: PopupSpec,
  isIn: Guard<In>,
): Promise<WindowLink<In, Out> | null> {
  return IS_SHELL ? connectTauri(spec, isIn) : connectBrowser(spec, isIn);
}

async function connectTauri<In extends { t: string }, Out>(
  spec: PopupSpec,
  isIn: Guard<In>,
): Promise<WindowLink<In, Out> | null> {
  const info = (window as unknown as Record<string, ShellPopupInfo | undefined>)[spec.initGlobal];
  if (!info) {
    return null;
  }
  const { emitTo } = await import('@tauri-apps/api/event');
  const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow');
  const me = getCurrentWebviewWindow();
  return {
    post: (msg) => void emitTo(info.host, spec.evtToHost, { key: info.key, msg }).catch(() => undefined),
    onMessage: (cb) => {
      const un = me.listen<unknown>(spec.evtToWin, (e) => {
        if (isIn(e.payload)) {
          cb(e.payload);
        }
      });
      return () => void un.then((f) => f());
    },
    close: () => undefined,
    focus: () => undefined,
  };
}

function connectBrowser<In extends { t: string }, Out>(spec: PopupSpec, isIn: Guard<In>): WindowLink<In, Out> | null {
  const key = new URLSearchParams(window.location.search).get(KEY_PARAM);
  const host = window.opener as Window | null;
  if (!key || !host) {
    return null;
  }
  const origin = window.location.origin;
  return {
    post: (msg) => {
      try {
        host.postMessage({ [spec.tag]: key, msg }, origin);
      } catch {
        // 세션 창이 사라졌다 — 감시가 end 를 만든다
      }
    },
    onMessage: (cb) => {
      const onMsg = (e: MessageEvent) => {
        const env = e.data as Record<string, unknown> | null;
        if (e.origin === origin && e.source === host && env?.[spec.tag] === key && isIn(env.msg)) {
          cb(env.msg);
        }
      };
      window.addEventListener('message', onMsg);
      // 세션 창(탭)이 닫히면 알려 줄 쪽이 없다 → 주기적으로 본다.
      const timer = window.setInterval(() => {
        if (host.closed) {
          window.clearInterval(timer);
          cb({ t: 'end' } as In);
        }
      }, CLOSED_POLL_MS);
      return () => {
        window.removeEventListener('message', onMsg);
        window.clearInterval(timer);
      };
    },
    close: () => undefined,
    focus: () => undefined,
  };
}
